import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/foundation/auth/AuthContext";
import { api, apiErr } from "@/foundation/api/client";
import { Input } from "@/components/ui/input";
import { KeyRound, LogOut } from "lucide-react";

/**
 * C3-B.2.7 · ChangePasswordGate
 *
 * Blocca l'accesso all'applicazione se `user.must_change_password === true`
 * finché l'utente non sceglie una nuova password (chiamata a POST
 * /api/auth/change-password). Al termine, aggiorna il context via setUser.
 *
 * Sostituisce completamente il rendering dei children finché il flag è attivo:
 * l'utente non può navigare, cliccare voci di menu, o effettuare azioni.
 */
export default function ChangePasswordGate({ children }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [exiting, setExiting] = useState(false);

  const mustChange = user && user.must_change_password;

  const exitToLogin = useCallback(async () => {
    if (!mustChange || loading || exiting) return;
    setExiting(true);
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
      setExiting(false);
    }
  }, [mustChange, loading, exiting, logout, navigate]);

  useEffect(() => {
    if (!mustChange) return undefined;
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      exitToLogin();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mustChange, exitToLogin]);

  if (!mustChange) return children;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) {
      setErr("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    if (next !== confirm) {
      setErr("La nuova password e la conferma non coincidono");
      return;
    }
    if (next === current) {
      setErr("La nuova password deve essere diversa da quella corrente");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password-short", {
        current_password: current, new_password: next,
      });
      // Aggiorna il context (senza rifare /auth/me — evitiamo race)
      setUser({ ...user, must_change_password: false });
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: "rgba(15,50,25,0.55)", backdropFilter: "blur(4px)" }}
      data-testid="must-change-password-gate"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-2 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "#4CAF5018", border: "1px solid #4CAF5033" }}
          >
            <KeyRound size={20} style={{ color: "#4CAF50" }} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-suite-neutral-400">
              Sicurezza
            </div>
            <div className="text-[17px] font-semibold text-suite-neutral-800">
              Imposta la tua password
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="px-6 pt-2 pb-6 space-y-4"
              data-testid="change-password-form">
          <p className="text-[13px] text-suite-neutral-600 leading-relaxed">
            Per motivi di sicurezza è obbligatorio cambiare la password al primo
            accesso. Scegli una password personale conosciuta solo a te.
          </p>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-suite-neutral-600 uppercase tracking-wider">
              Password corrente
            </label>
            <Input type="password" value={current}
                   onChange={(e) => setCurrent(e.target.value)} required
                   autoComplete="off"
                   data-testid="cp-current"
                   className="h-11 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-suite-neutral-600 uppercase tracking-wider">
              Nuova password (min. 8 caratteri)
            </label>
            <Input type="password" value={next}
                   onChange={(e) => setNext(e.target.value)} required
                   autoComplete="off"
                   data-testid="cp-new"
                   className="h-11 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-suite-neutral-600 uppercase tracking-wider">
              Conferma nuova password
            </label>
            <Input type="password" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} required
                   autoComplete="off"
                   data-testid="cp-confirm"
                   className="h-11 rounded-lg" />
          </div>
          {err && (
            <div className="text-sm text-suite-error bg-suite-error-light/60 border border-suite-error-border rounded-lg px-3 py-2"
                 data-testid="cp-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={loading}
                  data-testid="cp-submit"
                  className="w-full h-11 rounded-lg text-white font-semibold uppercase tracking-[0.12em] disabled:opacity-70"
                  style={{ background: "#4CAF50", fontFamily: "Montserrat, sans-serif", fontSize: "13px" }}>
            {loading ? "Salvataggio…" : "Salva e continua"}
          </button>
          <button type="button" onClick={exitToLogin}
                  disabled={loading || exiting}
                  data-testid="change-password-exit"
                  className="w-full h-10 rounded-lg border border-suite-neutral-200 text-suite-neutral-700 font-medium flex items-center justify-center gap-2 hover:bg-suite-neutral-50 disabled:opacity-60">
            <LogOut size={16} />
            {exiting ? "Uscita in corso…" : "Esci e torna al login"}
          </button>
          <p className="text-center text-[11px] text-suite-neutral-400">
            Puoi uscire anche premendo Esc. La password temporanea dovrà essere
            cambiata al prossimo accesso.
          </p>
        </form>
      </div>
    </div>
  );
}
